import React, { useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";
import BeneficiarySearchNSelect from "../../Eligibility/components/requestEligibility/BeneficiarySearchNSelect";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFilter, FaUndo } from "react-icons/fa";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppButton from "../../../shared/AppButton";

function ClaimsFilter({
  refetch,
  setLoading,
  setClaimsFilters,
  claimsFilters,
  activeTab,
  setAllClaims,
  setClaimsPages,
  setClaimsTotal,
  fromDate,
  toDate,
}) {
  const currentDate = new Date();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [payers, setPayers] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const addData = async (values) => {
    let obj = {
      from_date: values.fromDate,
      to_date: values.toDate,
      payer_id: values.payer,
      type: values.type,
      patient_id: values.beneficiary,
      batch_id: values.batchID,
      tp_ref_no: values.tpRefNo,
      outcome: activeTab,
      from_date_ordered: values?.fromDateOrdered,
      to_date_ordered: values?.toDateOrdered,
      status: values.status,
      request_bundle_id: values.bundleID,
      is_followup_req: values?.isFollowUp,
      limit: 10,
      page: 1,
    };
    try {
      setIsApplying(true);
      setLoading(true);
      await refetch({
        input: Object.keys(obj).reduce((acc, key) => {
          const value = obj[key];
          if (value !== "" && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {}),
      }).then(({ data }) => {
        setAllClaims(data?.listClaimTransactions?.data);
        setClaimsPages(data?.listClaimTransactions?.pages);
        setClaimsTotal(data?.listClaimTransactions?.total);
      });
    } finally {
      setLoading(false);
      setIsApplying(false);
    }
    setClaimsFilters(obj);
  };

  const handleResetForm = async (values) => {
    let obj = {
      from_date: fromDate,
      to_date: toDate,
      payer_id: "",
      type: "",
      patient_id: "",
      batch_id: "",
      tp_ref_no: "",
      outcome: activeTab,
      from_date_ordered: "",
      to_date_ordered: "",
      status: values.status,
      request_bundle_id: "",
      is_followup_req: "",
      limit: 10,
      page: 1,
    };
    try {
      setIsResetting(true);
      setLoading(true);
      await refetch({
        input: Object.keys(obj).reduce((acc, key) => {
          const value = obj[key];
          if (value !== "" && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {}),
      }).then(({ data }) => {
        setAllClaims(data?.listClaimTransactions?.data);
        setClaimsPages(data?.listClaimTransactions?.pages);
        setClaimsTotal(data?.listClaimTransactions?.total);
      });
    } finally {
      setLoading(false);
      setIsResetting(false);
    }
    setClaimsFilters(obj);
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const types = [
    { id: "institutional", name: t("Institutional") },
    { id: "oral", name: t("Dental") },
    { id: "pharmacy", name: t("Pharmacy") },
    { id: "professional", name: t("Professional") },
    { id: "vision", name: t("Optical") },
  ];

  const statuses = [
    { id: "approved", name: t("Approved") },
    { id: "partial Approved", name: t("Partial Approved") },
    { id: "rejected", name: t("Rejected") },
    { id: "pending", name: t("Pending") },
    { id: "pended", name: t("Pended") },
    { id: "cancelled", name: t("Cancelled") },
  ];

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      fromDate: claimsFilters?.from_date,
      toDate: claimsFilters?.to_date,
      fromDateOrdered: claimsFilters?.from_date_ordered,
      toDateOrdered: claimsFilters?.to_date_ordered,
      payer: claimsFilters?.payer_id,
      type: claimsFilters?.type,
      beneficiary: claimsFilters?.patient_id,
      status: claimsFilters?.from_date_ordered,
      bundleID: claimsFilters?.request_bundle_id,
      batchID: claimsFilters?.batch_id,
      tpRefNo: claimsFilters?.tp_ref_no,
      isFollowUp: claimsFilters?.is_followup_req,
      outcome: claimsFilters?.outcome,
      isValid: true,
    },
    onSubmit: (values) => {
      addData(values);
    },
    onReset: (values) => {
      handleResetForm(values);
    },
  });

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-1.5 gap-y-1.5 pb-2">
        <CustomFilterInput
          label={t("Submission Date")}
          name="fromDate"
          value={values.fromDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("From Date")}
          type="date"
        />
        <CustomFilterInput
          label={t("To Date")}
          name="toDate"
          value={values.toDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("To Date")}
          type="date"
        />
        <CustomFilterInput
          label={t("Ordered Date")}
          name="fromDateOrdered"
          value={values.fromDateOrdered}
          onChange={handleChange}
          placeholder={t("From Order Date")}
          type="text"
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => {
            e.target.type = "text";
            handleBlur(e);
          }}
        />
        <CustomFilterInput
          label={t("To Order Date")}
          name="toDateOrdered"
          value={values.toDateOrdered}
          onChange={handleChange}
          placeholder={t("To Order Date")}
          type="text"
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => {
            e.target.type = "text";
            handleBlur(e);
          }}
        />
        <CustomFilterInput
          label={t("Select Payer")}
          name="payer"
          value={values.payer}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Payer")}
          isSelect
          renderOptions={() =>
            payers.map((category) => (
              <optgroup
                key={category.id}
                label={
                  category?.name_en
                    ? `${category?.name_en} (${category?.license_id})`
                    : ""
                }
              >
                {category?.payers?.map((insurance, idx) => (
                  <option key={`${insurance.id}-${idx}`} value={insurance.id}>
                    {insurance?.name_en} ({insurance?.license_id})
                  </option>
                ))}
              </optgroup>
            ))
          }
        />
        <CustomFilterInput
          label={t("Select Type")}
          name="type"
          value={values.type}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Type")}
          isSelect
          options={types}
        />
        <CustomFilterInput
          label={t("Batch ID")}
          name="batchID"
          value={values.batchID}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Batch ID")}
          type="text"
        />
        <CustomFilterInput
          label={t("Claim Reference")}
          name="tpRefNo"
          value={values.tpRefNo}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Claim Reference")}
          type="text"
        />
        <CustomFilterInput
          label={t("Select Status")}
          name="status"
          value={values.status}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Status")}
          isSelect
          options={statuses}
        />
        <CustomFilterInput
          label={t("Request Bundle ID")}
          name="bundleID"
          value={values.bundleID}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Request Bundle ID")}
          type="text"
        />

        {/* Is Follow Up Toggle */}
        <div className="flex flex-col">
          <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
            {t("Is Follow Up")}
          </label>
          <div className="flex justify-between items-center bg-slate-100/50 border !border-slate-200 rounded-[6px] px-2 !h-[28px] !py-0">
            <div className="text-xs font-medium text-gray-700 capitalize tracking-tight">
              {t("Is Follow Up")}
            </div>
            <div className="flex items-center">
              <label
                htmlFor="toggle-followup"
                className="relative inline-flex items-center cursor-pointer scale-[0.65] origin-right"
              >
                <input
                  type="checkbox"
                  id="toggle-followup"
                  name="isFollowUp"
                  className="sr-only peer"
                  checked={values.isFollowUp}
                  onChange={handleChange}
                />
                <div
                  className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer
                    peer-checked:!bg-[#0070C2]
                    peer-checked:after:translate-x-[20px]
                    rtl:peer-checked:after:-translate-x-[20px]
                    after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all after:duration-300"
                ></div>
              </label>
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize truncate">
            {t("Search Patient")}
          </label>
          <BeneficiarySearchNSelect
            setFieldValue={(val) => {
              setFieldValue("beneficiary", val?.id);
            }}
            name="eligibility"
          />
        </div>
        <div className="flex justify-end gap-2 items-end">
          <AppButton
            title={t("Reset")}
            variant="reset"
            onClick={resetForm}
            startIcon={<FaUndo className="!text-[12px]" />}
            className="hover:!bg-slate-100"
            isLoading={isResetting}
            loadingText=""
          />
          <AppButton
            title={t("Apply Filter")}
            onClick={handleSubmit}
            startIcon={<FaFilter className="!text-[12px]" />}
            isLoading={isApplying}
            loadingText=""
          />
        </div>
      </div>
    </div>
  );
}

export default React.memo(ClaimsFilter);
